<script lang="ts" setup>

import { onBeforeUnmount, onMounted, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    customStyles?: object
  }>(), {
    customStyles: () => {
      return {}
    }
  }
)
const stickyContainer = ref<HTMLElement | null>(null)
const minHeight = ref(10)
const width = ref<number | null>(null)
const padding = ref(10)
const position = ref<'fixed' | 'relative'>('relative')
const top = ref(0)
const initialY = ref(0)
const scrolling = ref(false)
const stickySide = ref(0)
const currentScroll = ref(0)
// The content height changes without window resize (e.g. "show more"): keep min-height in sync,
// otherwise the page shrinks when the content gets fixed and the scroll loops between fixed/relative
const contentResizeObserver = new ResizeObserver(updateWidth)

onMounted(() => {
  updateWidth()
  onScroll()

  window.addEventListener('resize', updateWidth)
  window.addEventListener('scroll', onScroll)
  contentResizeObserver.observe(stickyContainer.value.children[0])
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWidth)
  window.removeEventListener('scroll', onScroll)
  contentResizeObserver.disconnect()
})

function updateWidth() {
  const elementStyle = getComputedStyle(stickyContainer.value)
  width.value = stickyContainer.value.offsetWidth - parseFloat(elementStyle.paddingLeft) - parseFloat(elementStyle.paddingRight)
  const { height } = stickyContainer.value.children[0].getBoundingClientRect()
  minHeight.value = height
}

function onScroll() {
  if (scrolling.value || !stickyContainer.value) {
    return
  }
  scrolling.value = true
  window.requestAnimationFrame(setTopStyle)
}

function stickTop() {
  if (position.value !== 'fixed' || top.value !== padding.value || stickySide.value !== -1) {
    position.value = 'fixed'
    top.value = padding.value
    stickySide.value = -1
  }
}

function stickBottom(height) {
  // In case the element is smaller than the window
  const newTop = Math.min(window.innerHeight - height - padding.value, padding.value)
  if (position.value !== 'fixed' || top.value !== newTop || stickySide.value !== 1) {
    top.value = newTop
    position.value = 'fixed'
    stickySide.value = 1
  }
}

function unStick(dy) {
  if (position.value !== 'relative' || top.value !== dy || stickySide.value !== 0) {
    position.value = 'relative'
    top.value = dy
    stickySide.value = 0
  }
}

function setTopStyle() {
  if (!stickyContainer.value || !stickyContainer.value.children[0]) {
    scrolling.value = false
    return
  }

  const { top, bottom, height } = stickyContainer.value.children[0].getBoundingClientRect()
  if (!initialY.value) {
    initialY.value = window.scrollY + top
  }

  if (window.scrollY + padding.value < initialY.value) {
    // we are above the element's initial position
    if (stickySide.value !== 0) {
      unStick(0)
    }
  } else {
    if (window.scrollY > currentScroll.value) {
      // scrolling down.

      if (height + 2 * padding.value <= window.innerHeight) {
        // If height < window.innerHeight we stick to the top
        if (stickySide.value !== -1) {
          stickTop()
        }
      } else {
        // If height > window.innerHeight

        if (stickySide.value === 0 && bottom + padding.value < window.innerHeight) {
          // we either stick the bottom if we reached it
          stickBottom(height)
        } else if (stickySide.value === -1) {
          // or we unStick if the top is stuck
          unStick(window.scrollY - initialY.value + padding.value)
        }
      }
    } else if (window.scrollY < currentScroll.value) {
      // scrolling up

      if (stickySide.value !== -1 && height + 2 * padding.value <= window.innerHeight) {
        // height < window.innerHeight, we stick to top
        stickTop()
      } else {
        // height > window.innerHeight
        if (stickySide.value === 0 && top - padding.value > 0) {
          // we either stick the top if we reached it
          stickTop()
        } else if (stickySide.value === 1) {
          // or we unStick if the bottom is stuck
          unStick(window.scrollY - initialY.value + top)
        }
      }
    }
  }

  currentScroll.value = window.scrollY
  scrolling.value = false
}
</script>

<template>
  <div
    ref="stickyContainer"
    :style="{ 'min-height': `${minHeight}px` }"
    class="sticky-container"
  >
    <div
      :class="{ fixing: position === 'fixed' }"
      :style="{ position, width: `${width}px`, top: `${top}px`, ...customStyles }"
      class="sticky"
    >
      <slot />
    </div>
  </div>
</template>
